/**
 * webWorker + setInterval 版本的 useServerTime Hook
 * 通过 Web Worker 来处理时间同步和更新，减少主线程负担
 * 使用 Day.js 进行时间格式化，强制使用 +8 时区
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import emitter from '@/utils/eventBus';
import { SYNC_SERVER_TIME } from '@/constants/event-bus';
import { globalCache } from '@/utils/utils';

export interface ServerTimeResponse {
  timestamp: number;
}

export interface TimeFormatOptions {
  hour12?: boolean;
  showSeconds?: boolean;
  showMilliseconds?: boolean;
  format?: string;
}

export interface UseServerTimeReturn {
  serverTime: number | null;
  isSynced: boolean;
  isSyncing: boolean;
  lastSync: number | null;
  timeDiff: number;
  syncWithServer: () => Promise<void>;
  getServerTime: () => number;
  formatTime: (timestamp?: number | null, options?: TimeFormatOptions) => string;
  formatDate: (timestamp?: number | null, format?: string) => string;
  getDayjs: (timestamp?: number | null) => dayjs.Dayjs;
}

export interface WorkerMessage {
  type: 'init' | 'update' | 'stop' | 'timeUpdate';
  data?: any;
}

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

// 设置默认时区为+8
dayjs.tz.setDefault('Asia/Shanghai');

// 模拟服务器API调用
const fetchServerTime = async (): Promise<ServerTimeResponse> => {
  try {
    // 获取服务器的时间
    const response = await fetch(`/?now=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    // 获取服务器时间
    const serverDate = response.headers.get('date');
    return {
      timestamp: Date.parse(serverDate!),
    };
  } catch (error) {
    return {
      timestamp: Date.now(),
    };
  }
};

// 创建Web Worker
const createTimeWorker = (): Worker => {
  // 创建Web Worker的代码字符串
  const workerCode = `
    let serverTime = null;
    let timeInterval = null;
    let lastUpdate = null;

    self.onmessage = function(e) {
      const { type, data } = e.data;

      switch (type) {
        case 'init':
          serverTime = data.initialTime;
          lastUpdate = performance.now();

          if (timeInterval) {
            clearInterval(timeInterval);
          }

          timeInterval = setInterval(() => {
            if (lastUpdate === null || serverTime === null) return;

            const now = performance.now();
            const elapsed = now - lastUpdate;
            lastUpdate = now;

            serverTime += elapsed;

            self.postMessage({
              type: 'timeUpdate',
              data: { serverTime }
            });
          }, 60000);
          break;

        case 'update':
          serverTime = data.serverTime;
          lastUpdate = performance.now();
          break;

        case 'stop':
          if (timeInterval) {
            clearInterval(timeInterval);
            timeInterval = null;
          }
          break;
      }
    };
  `;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

// 计算时间差
export const calcDiffTIme = (
  serverTime?: number,
): {
  clientTime: number;
  timeDiff: number;
  serverTime: number;
} => {
  if (!serverTime) serverTime = Date.now();
  const clientTime = Date.now();
  const timeDiff = serverTime - clientTime;
  return {
    clientTime,
    timeDiff,
    serverTime,
  };
};

// 触发同步服务器时间事件
export const syncServerTimeEvent = (): void => {
  emitter.emit(SYNC_SERVER_TIME);
};

// 获取当前服务器时间等信息
export const getServerTimeInfo = (): {
  serverTime: number;
  timeDiff: number;
  lastSync: number | null;
  isSynced: boolean;
  isSyncing: boolean;
} | null => globalCache.get(SYNC_SERVER_TIME);

// 自定义Hook：useServerTime
export const useServerTime = (): UseServerTimeReturn => {
  const [serverTime, setServerTime] = useState<number | null>(null);
  const [isSynced, setIsSynced] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [timeDiff, setTimeDiff] = useState<number>(0);

  const workerRef = useRef<Worker | null>(null);
  const isActiveRef = useRef<boolean>(true);
  const timeDiffRef = useRef<number>(0);
  const isInitRef = useRef<boolean>(false);

  // 初始化Web Worker
  const initWorker = useCallback((initialTime: number) => {
    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'init',
        data: { initialTime },
      } as WorkerMessage);
    }
  }, []);

  // 同步服务器时间
  const syncWithServer = useCallback(async (): Promise<void> => {
    setIsSyncing(true);

    try {
      const startTime = performance.now();
      const response = await fetchServerTime();
      const endTime = performance.now();

      const roundTripTime = endTime - startTime;
      const estimatedServerTime = response.timestamp + roundTripTime / 2;

      // 计算客户端与服务器的时间差
      const { clientTime, timeDiff: newTimeDiff } = calcDiffTIme(estimatedServerTime);

      timeDiffRef.current = newTimeDiff;
      setIsSynced(true);
      setLastSync(clientTime);
      setTimeDiff(newTimeDiff);

      // 更新Web Worker中的时间
      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'update',
          data: { serverTime: estimatedServerTime },
        } as WorkerMessage);
      }

      // 设置初始服务器时间
      setServerTime(estimatedServerTime);
      globalCache.set(SYNC_SERVER_TIME, {
        ...(globalCache.get(SYNC_SERVER_TIME) || {}),
        serverTime: estimatedServerTime,
        isSynced: true,
        isSyncing: false,
        lastSync: clientTime,
        timeDiff: newTimeDiff,
      });
    } catch (error) {
      setIsSynced(false);
      globalCache.set(SYNC_SERVER_TIME, {
        ...(globalCache.get(SYNC_SERVER_TIME) || {}),
        isSynced: false,
      });
    } finally {
      setIsSyncing(false);
      globalCache.set(SYNC_SERVER_TIME, {
        ...(globalCache.get(SYNC_SERVER_TIME) || {}),
        isSyncing: false,
      });
    }
  }, []);

  // 初始化Web Worker
  useEffect(() => {
    workerRef.current = createTimeWorker();

    // 处理Web Worker发送的消息
    workerRef.current.onmessage = (e: MessageEvent<WorkerMessage>) => {
      const { type, data } = e.data;

      if (type === 'timeUpdate') {
        // TODO serverTime 得换一种方式获取，目前暂时注释，后面再优化
        // setServerTime(serverTime); // !NOTE 这个部分会导致 tabLayout 组件异常，可能是更新太频繁了

        const { clientTime, timeDiff: newTimeDiff } = calcDiffTIme(data.serverTime);
        globalCache.set(SYNC_SERVER_TIME, {
          ...(globalCache.get(SYNC_SERVER_TIME) || {}),
          serverTime: data.serverTime,
          timeDiff: newTimeDiff,
          lastSync: clientTime,
        });
      }
    };

    // 初始同步
    syncWithServer();

    // 处理页面可见性变化
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        // 页面变为可见，立即同步
        isActiveRef.current = true;
        syncWithServer();
      } else {
        // 页面变为隐藏
        isActiveRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    emitter.on(SYNC_SERVER_TIME, syncWithServer);

    return () => {
      // 清理工作
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'stop' } as WorkerMessage);
        workerRef.current.terminate();
      }
    };
  }, [syncWithServer]);

  // 当serverTime更新时，确保Web Worker已经初始化
  useEffect(() => {
    if (serverTime !== null && workerRef.current && !isInitRef.current) {
      isInitRef.current = true;
      // 确保Web Worker已经启动定时器
      initWorker(serverTime);
    }
  }, [serverTime, initWorker]);

  // 使用Day.js格式化时间（强制+8时区）
  const formatTime = useCallback(
    (timestamp?: number | null, options: TimeFormatOptions = {}): string => {
      if (!timestamp) return '--:--:--';

      const { hour12 = false, showSeconds = true, showMilliseconds = false, format = '' } = options;

      // 使用dayjs处理时间，强制转换为+8时区
      let time = dayjs(timestamp).tz('Asia/Shanghai');

      if (format) {
        return time.format(format);
      }

      if (hour12) {
        return time.format(showSeconds ? 'hh:mm:ss A' : 'hh:mm A');
      } else {
        let formatStr = 'HH:mm';
        if (showSeconds) formatStr += ':ss';
        if (showMilliseconds) formatStr += '.SSS';
        return time.format(formatStr);
      }
    },
    [],
  );

  // 使用Day.js格式化日期（强制+8时区）
  const formatDate = useCallback(
    (timestamp?: number | null, format: string = 'YYYY年MM月DD日 dddd'): string => {
      if (!timestamp) return '----年--月--日 星期-';

      // 使用dayjs处理日期，强制转换为+8时区
      return dayjs(timestamp).tz('Asia/Shanghai').format(format);
    },
    [],
  );

  // 获取当前服务器时间
  const getServerTime = useCallback((): number => {
    return serverTime || Date.now() + timeDiffRef.current;
  }, [serverTime]);

  // 获取Day.js对象（强制+8时区）
  const getDayjs = useCallback(
    (timestamp?: number | null): dayjs.Dayjs => {
      return dayjs(timestamp || serverTime || Date.now() + timeDiffRef.current).tz('Asia/Shanghai');
    },
    [serverTime],
  );

  return {
    serverTime,
    isSynced,
    isSyncing,
    lastSync,
    timeDiff,
    syncWithServer,
    getServerTime,
    formatTime,
    formatDate,
    getDayjs,
  };
};

export default useServerTime;
